import React from "react";
import { useTranslation } from "react-i18next";
import { Grid, Skeleton, Box } from "@mui/material";
import MetricCard from "../../../../components/SurveyReportCard/MetricCard";
import MetricsOverTime from "../../../../components/SurveyReportCard/MetricsOverTime";
import RatingDistribution from "../../../../components/SurveyReportCard/RatingDistribution";

import {
    TIME_PERIODS,
    GRANULARITY
} from "../../../../constants/PatientSurveyReportConstants";

const MetricsSection = ({
    reportData,
    hasMetricsForPeriod,
    periodStart,
    periodEnd,
    averageRatingTrend,
    totalResponsesTrend,
    satisfactionRateTrend,
    issuesReportedTrend,
    styles,
    activeTimeframe,
    loading
}) => {
    const { t } = useTranslation();

    return (
        <>
            <Grid container spacing={2} alignItems="stretch" className={styles.metricsContainer}>
                <Grid item xs={12} sm={6} md={3}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                    ) : (
                        <MetricCard
                            title={t("reports.patientSurveyReportPage.metrics.averageRating")}
                            value={
                                reportData?.summary?.averageRating != null
                                    ? reportData.summary.averageRating.toFixed(1)
                                    : "-"
                            }
                            subtitle=""
                            trend={
                                averageRatingTrend == null
                                    ? undefined
                                    : `${averageRatingTrend > 0 ? "+" : ""}${averageRatingTrend.toFixed(2)}`
                            }
                            trendValue={averageRatingTrend ?? null}
                            isRating
                            isPositive={(averageRatingTrend ?? 0) >= 0}
                        />
                    )}
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                    ) : (
                        <MetricCard
                            title={t("reports.patientSurveyReportPage.metrics.totalResponses")}
                            value={reportData?.summary?.totalResponses ?? "-"}
                            subtitle={t("reports.patientSurveyReportPage.metrics.totalResponsesSubtitle")}
                            trend={
                                reportData?.summary?.totalResponsesTrend == null
                                    ? undefined
                                    : `${reportData.summary.totalResponsesTrend > 0 ? "+" : ""}${reportData.summary.totalResponsesTrend}`
                            }
                            trendValue={totalResponsesTrend ?? null}
                            isPositive={(totalResponsesTrend ?? 0) >= 0}
                        />
                    )}
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                    ) : (
                        <MetricCard
                            title={t("reports.patientSurveyReportPage.metrics.satisfactionRate")}
                            value={
                                reportData?.summary?.satisfactionRate
                                    ? `${Math.round(reportData.summary.satisfactionRate * 100)}%`
                                    : "-"
                            }
                            subtitle={t("reports.patientSurveyReportPage.metrics.satisfactionRateSubtitle")}
                            trend={
                                satisfactionRateTrend == null
                                    ? undefined
                                    : `${satisfactionRateTrend > 0 ? "+" : ""}${Number(satisfactionRateTrend).toFixed(0)}`
                            }
                            trendValue={satisfactionRateTrend ?? null}
                            isPositive={(satisfactionRateTrend ?? 0) >= 0}
                        />
                    )}
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                    ) : (
                        <MetricCard
                            title={t("reports.patientSurveyReportPage.metrics.issuesReported")}
                            value={reportData?.summary?.issuesReported ?? "-"}
                            subtitle={t("reports.patientSurveyReportPage.metrics.issuesReportedSubtitle")}
                            trend={
                                reportData?.summary?.issuesReportedTrend == null
                                    ? undefined
                                    : `${reportData.summary.issuesReportedTrend > 0 ? "+" : ""}${reportData.summary.issuesReportedTrend}`
                            }
                            trendValue={issuesReportedTrend ?? null}
                            isPositive={(issuesReportedTrend ?? 0) <= 0}
                        />
                    )}
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="stretch" sx={{ mt: 0, px: 0 }}>
                <Grid item xs={12} md={8}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                    ) : hasMetricsForPeriod ? (
                        <MetricsOverTime
                            data={reportData?.metricsOverTime ?? []}
                            granularity={
                                activeTimeframe.timePeriod === TIME_PERIODS.MONTHLY
                                    ? GRANULARITY.WEEKLY
                                    : GRANULARITY.MONTHLY
                            }
                            periodStart={periodStart}
                            periodEnd={periodEnd}
                        />
                    ) : null}
                </Grid>

                
                <Grid item xs={12} md={4}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                    ) : (
                        <RatingDistribution
                            data={reportData?.ratingDistribution ?? []}
                            averageRating={reportData?.summary?.averageRating ?? 0}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default MetricsSection;
